import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Flame,
  Layers,
  Search,
  TrendingDown,
  TrendingUp,
  Wallet,
  XCircle,
} from "lucide-react";
import * as React from "react";

import type { DealDashboardMetrics, ManagerDealListItem } from "@/api/client";
import { getTeamDashboard, getTeamDeals } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { DealAdvancedFilters } from "@/components/deals/DealAdvancedFilters";
import {
  EMPTY_DEAL_FILTERS,
  matchesDealFilters,
  type DealAdvancedFilterState,
} from "@/components/deals/dealFilters";
import { DealDetailDrawer } from "@/components/deals/DealDetailDrawer";
import { PoDealLedger } from "@/components/po-insights/PoDealLedger";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { FormError } from "@/components/ui/form-error";
import { daysToClose, isExpiringWithin } from "@/lib/dealExpiry";
import { formatAudCents } from "@/lib/spif";

/** The three deal-backed drill-down drawers behind the Insights metric cards.
 *  - total-sales    → CHP-495: Total Sales Closed YTD (won/lost YTD sub-cards).
 *  - active-pipeline → CHP-494: open-pipeline value + active-deal volume.
 *  - expiring        → CHP-493: live deals expiring ≤30 days, most-critical first. */
export type DealMetricMode = "total-sales" | "active-pipeline" | "expiring";

/** Where the drawer's metrics + deal feed come from. The DEFAULT preserves exact
 * PO behaviour (org-scoped `getTeamDashboard` / `getTeamDeals`); the CM/RM/RA
 * Program Insights surface passes the program-wide `fetchDealDashboard`
 * (/deals/dashboard) + `listManagerDeals` (/deals/manager-list) so the same
 * drawer renders the whole channel rather than one org. `queryKeyScope` keeps the
 * two feeds in separate React-Query caches; `showPartnerColumn` adds the leading
 * Partner (org) column to the ledger for the multi-org program view. */
export interface DealMetricDataSource {
  fetchMetrics: (token: string) => Promise<DealDashboardMetrics>;
  fetchDeals: (token: string) => Promise<ManagerDealListItem[]>;
  /** Stable cache-scope label. The PO default ("team") keeps the existing keys
   * byte-identical (["deals","team-dashboard",token] / ["deals","team-list","all",token]). */
  queryKeyScope: string;
  /** Render the leading Partner (org) column — on for the program view. */
  showPartnerColumn?: boolean;
}

/** PO default — the org-scoped team feed. Omitting `dataSource` yields exactly
 * this, so the PO Insights surface is unchanged. */
export const PO_DEAL_METRIC_SOURCE: DealMetricDataSource = {
  fetchMetrics: (token) => getTeamDashboard(token),
  fetchDeals: (token) => getTeamDeals(token, "all"),
  queryKeyScope: "team",
};

export interface DealMetricDrawerProps {
  /** The active mode, or null when no deal-metric drawer is open. */
  mode: DealMetricMode | null;
  onClose: () => void;
  /** Where the metrics + deal feed come from. Defaults to the PO org-scoped feed
   * so the PO surface is unchanged; the program page overrides it. */
  dataSource?: DealMetricDataSource;
}

const EXPIRING_WINDOW_DAYS = 30;
const URGENCY_WINDOW_DAYS = 7;
const SEARCH_DEBOUNCE_MS = 250;

const TITLES: Record<DealMetricMode, { title: string; description: string }> = {
  "total-sales": {
    title: "Total Sales Closed YTD",
    description: "Won and lost deals closed this calendar year.",
  },
  "active-pipeline": {
    title: "Active Pipeline",
    description: "Open, live deals across your organisation.",
  },
  expiring: {
    title: "Expiring Deals",
    // Review-r2 (1e8e00a5): exact descriptor copy.
    description: "Live deals closing within the next 30 days.",
  },
};

/** Review-r2 (11dff804): the Total Sales Closed YTD ledger gets an All /
 * Closed-won / Closed-lost chip filter at the top-left. */
type TotalSalesFilter = "all" | "closed_won" | "closed_lost";
const TOTAL_SALES_CHIPS: { value: TotalSalesFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "closed_won", label: "Closed-won" },
  { value: "closed_lost", label: "Closed-lost" },
];

/** Review-r2 (ceafbc7d): the Active Pipeline ledger includes declined deal
 * registrations behind an All / Live / Declined chip filter. The page lands on
 * Live only by default. */
type PipelineFilter = "all" | "live" | "declined";
const PIPELINE_CHIPS: { value: PipelineFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "live", label: "Live" },
  { value: "declined", label: "Declined" },
];

/**
 * Deal-metric drill-down drawer (Insights redesign C2). A 65vw right Sheet with
 * mode-specific summary sub-cards over a reusable PoDealLedger, plus a search
 * box and an Advanced-Filters panel. The metrics row reads the org-wide team
 * dashboard aggregate; the ledger reads the org-scoped team deal feed and
 * filters client-side per mode.
 *
 * Row click layers the master-record detail (DealDetailDrawer) over this drawer.
 */
export function DealMetricDrawer({
  mode,
  onClose,
  dataSource = PO_DEAL_METRIC_SOURCE,
}: DealMetricDrawerProps) {
  const { accessToken } = useCurrentUser();
  const open = mode !== null;

  const metricsQuery = useQuery<DealDashboardMetrics>({
    queryKey: ["deals", `${dataSource.queryKeyScope}-dashboard`, accessToken],
    queryFn: () => dataSource.fetchMetrics(accessToken!),
    enabled: !!accessToken && open,
  });

  const dealsQuery = useQuery<ManagerDealListItem[]>({
    queryKey: ["deals", `${dataSource.queryKeyScope}-list`, "all", accessToken],
    queryFn: () => dataSource.fetchDeals(accessToken!),
    enabled: !!accessToken && open,
  });

  const [filters, setFilters] = React.useState<DealAdvancedFilterState>(EMPTY_DEAL_FILTERS);
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [detailDealId, setDetailDealId] = React.useState<string | null>(null);
  // Review-r2: per-mode status chip filters. Total-sales defaults to All;
  // active-pipeline defaults to Live (the page shows live deals first).
  const [totalSalesFilter, setTotalSalesFilter] = React.useState<TotalSalesFilter>("all");
  const [pipelineFilter, setPipelineFilter] = React.useState<PipelineFilter>("live");

  // Re-seed search + filters each time the drawer opens (or the mode changes).
  React.useEffect(() => {
    if (open) {
      setFilters(EMPTY_DEAL_FILTERS);
      setSearchInput("");
      setDebouncedSearch("");
      setTotalSalesFilter("all");
      setPipelineFilter("live");
    }
  }, [open, mode]);

  React.useEffect(() => {
    if (searchInput === "") {
      setDebouncedSearch("");
      return;
    }
    const t = window.setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const allDeals = React.useMemo(() => dealsQuery.data ?? [], [dealsQuery.data]);
  const now = React.useMemo(() => Date.now(), []);
  // Calendar-YTD boundary (UTC Jan 1), matching the backend's closed_*_ytd gate.
  const yearStartMs = React.useMemo(() => Date.UTC(new Date(now).getUTCFullYear(), 0, 1), [now]);
  const metrics = metricsQuery.data;
  const query = debouncedSearch.trim().toLowerCase();

  // Mode-scoped base set for the ledger (pre-advanced-filter / pre-search).
  // Review-r2: per-mode status chips narrow this set — total-sales All/won/lost,
  // active-pipeline All/Live/Declined (defaults Live).
  const baseDeals = React.useMemo(() => {
    switch (mode) {
      case "total-sales": {
        // YTD closed deals, won AND lost — matches the four YTD sub-cards and
        // the "Closed YTD" title. closed_at is the UTC terminal timestamp.
        const rows = allDeals.filter(
          (d) =>
            (d.deal_status === "closed_won" || d.deal_status === "closed_lost") &&
            d.closed_at != null &&
            new Date(d.closed_at).getTime() >= yearStartMs,
        );
        if (totalSalesFilter === "all") return rows;
        return rows.filter((d) => d.deal_status === totalSalesFilter);
      }
      case "active-pipeline": {
        // Live + declined registrations; the chip filter lands on Live first.
        const rows = allDeals.filter((d) => d.deal_status === "live" || d.deal_status === "denied");
        if (pipelineFilter === "all") return rows;
        return pipelineFilter === "declined"
          ? rows.filter((d) => d.deal_status === "denied")
          : rows.filter((d) => d.deal_status === "live");
      }
      case "expiring": {
        // Live + expiring ≤30 days, most-critical (soonest-closing) first.
        const rows = allDeals.filter((d) => isExpiringWithin(d, now, EXPIRING_WINDOW_DAYS));
        return [...rows].sort((a, b) => (daysToClose(a, now) ?? 0) - (daysToClose(b, now) ?? 0));
      }
      default:
        return [];
    }
  }, [allDeals, mode, now, yearStartMs, totalSalesFilter, pipelineFilter]);

  const visibleDeals = React.useMemo(() => {
    const filtered = baseDeals.filter((d) =>
      matchesDealFilters(filters, {
        submittedAt: d.submission_timestamp,
        closingAt: d.target_close_date,
        valueCents: d.total_contract_value,
        partnerOrg: d.channel_partner_company,
        owner: d.lead_bdm_name,
      }),
    );
    if (query === "") return filtered;
    return filtered.filter(
      (d) =>
        d.client_name.toLowerCase().includes(query) ||
        (d.deal_id_display ?? "").toLowerCase().includes(query) ||
        (d.lead_bdm_name ?? "").toLowerCase().includes(query),
    );
  }, [baseDeals, filters, query]);

  const ownerOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const d of allDeals) if (d.lead_bdm_name) set.add(d.lead_bdm_name);
    return [...set].sort();
  }, [allDeals]);

  // Mode-specific summary sub-cards.
  const summary = React.useMemo(() => {
    if (!mode) return [];
    if (mode === "total-sales") {
      // Review-r2 (6f8b89f7): exact card titles.
      return [
        {
          testId: "total-sales-won-value",
          title: "Closed-Won Total Value",
          value: formatAudCents(metrics?.closed_won_value_ytd ?? 0),
          subtitle: undefined as string | undefined,
          tone: "success" as const,
          icon: TrendingUp,
        },
        {
          testId: "total-sales-won-volume",
          title: "Closed-Won Total Volume",
          value: String(metrics?.closed_won_count_ytd ?? 0),
          subtitle: undefined,
          tone: "success" as const,
          icon: CheckCircle2,
        },
        {
          testId: "total-sales-lost-value",
          title: "Closed-Lost Total Value",
          value: formatAudCents(metrics?.closed_lost_value_ytd ?? 0),
          subtitle: undefined,
          tone: "neutral" as const,
          icon: TrendingDown,
        },
        {
          testId: "total-sales-lost-volume",
          title: "Closed-Lost Total Volume",
          value: String(metrics?.closed_lost_count_ytd ?? 0),
          subtitle: undefined,
          tone: "neutral" as const,
          icon: XCircle,
        },
      ];
    }
    if (mode === "active-pipeline") {
      return [
        {
          testId: "active-pipeline-value",
          title: "Total Pipeline Value",
          // CHP-494: "Total $ value of all LIVE deals" — LIVE-only, matching the
          // sibling Pipeline Volume (active_deals) and the LIVE-only ledger below.
          // open_pipeline_value summed PENDING_REVIEW+LIVE+EXTENSION_PENDING+WIN_PENDING_REVIEW.
          value: formatAudCents(metrics?.active_deals_value ?? 0),
          subtitle: undefined,
          tone: "neutral" as const,
          icon: Wallet,
        },
        {
          testId: "active-pipeline-volume",
          title: "Pipeline Volume",
          value: String(metrics?.active_deals ?? 0),
          subtitle: undefined,
          tone: "neutral" as const,
          icon: Layers,
        },
      ];
    }
    // expiring — risk value/count over the ≤30-day base set + urgency (≤7d).
    const riskValue = baseDeals.reduce((sum, d) => sum + (d.total_contract_value ?? 0), 0);
    const urgent = baseDeals.filter((d) => isExpiringWithin(d, now, URGENCY_WINDOW_DAYS)).length;
    return [
      {
        testId: "expiring-risk-value",
        title: "Total Risk Value",
        value: formatAudCents(riskValue),
        subtitle: undefined,
        tone: "neutral" as const,
        icon: AlertTriangle,
      },
      {
        testId: "expiring-deal-count",
        // CHP-828/829: "Deal Count" → "Total expiring".
        title: "Total expiring",
        value: String(baseDeals.length),
        subtitle: "expiring ≤30 days",
        tone: "neutral" as const,
        icon: CalendarClock,
      },
      {
        testId: "expiring-urgency-index",
        // CHP-828/829: "Urgency Index" → "Need attention".
        title: "Need attention",
        value: String(urgent),
        subtitle: urgent > 0 ? "closing ≤7 days" : undefined,
        tone: "neutral" as const,
        icon: Flame,
      },
    ];
  }, [mode, metrics, baseDeals, now]);

  const copy = mode ? TITLES[mode] : null;
  const gridCols =
    summary.length === 4
      ? "sm:grid-cols-4"
      : summary.length === 2
        ? "sm:grid-cols-2"
        : "sm:grid-cols-3";

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:w-[65vw] sm:max-w-[65vw]"
        data-testid="deal-metric-drawer"
      >
        <SheetHeader>
          <SheetTitle className="pr-8">{copy?.title ?? "Deals"}</SheetTitle>
          <SheetDescription>{copy?.description ?? ""}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className={`grid grid-cols-1 gap-3 ${gridCols}`}>
            {summary.map((card) => (
              <StatCard
                key={card.testId}
                title={card.title}
                value={card.value}
                subtitle={card.subtitle}
                tone={card.tone}
                icon={card.icon}
                testId={card.testId}
              />
            ))}
          </div>

          {/* Review-r2: mode-specific status chips on the top-left of the
              ledger — total-sales (All/Closed-won/Closed-lost) and
              active-pipeline (All/Live/Declined). */}
          {mode === "total-sales" ? (
            <div
              className="flex flex-wrap items-center gap-2"
              role="group"
              aria-label="Filter closed deals"
            >
              {TOTAL_SALES_CHIPS.map((chip) => (
                <Button
                  key={chip.value}
                  type="button"
                  size="sm"
                  variant={totalSalesFilter === chip.value ? "default" : "outline"}
                  onClick={() => setTotalSalesFilter(chip.value)}
                  data-testid={`total-sales-filter-${chip.value}`}
                >
                  {chip.label}
                </Button>
              ))}
            </div>
          ) : null}
          {mode === "active-pipeline" ? (
            <div
              className="flex flex-wrap items-center gap-2"
              role="group"
              aria-label="Filter pipeline deals"
            >
              {PIPELINE_CHIPS.map((chip) => (
                <Button
                  key={chip.value}
                  type="button"
                  size="sm"
                  variant={pipelineFilter === chip.value ? "default" : "outline"}
                  onClick={() => setPipelineFilter(chip.value)}
                  data-testid={`pipeline-filter-${chip.value}`}
                >
                  {chip.label}
                </Button>
              ))}
            </div>
          ) : null}

          {/* Review-r2: search leads; Advanced filters sits to its RIGHT. */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative w-full sm:w-72">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by client, deal ID, or owner…"
                aria-label="Search deals"
                className="pl-9"
                data-testid="deal-metric-search"
              />
            </div>
            <DealAdvancedFilters
              value={filters}
              onChange={setFilters}
              ownerOptions={ownerOptions}
            />
          </div>

          {dealsQuery.isLoading ? (
            <p role="status" className="text-sm text-muted-foreground">
              Loading deals…
            </p>
          ) : dealsQuery.error ? (
            <FormError>Couldn&apos;t load your organisation&apos;s deals.</FormError>
          ) : (
            <PoDealLedger
              deals={visibleDeals}
              onDealClick={setDetailDealId}
              showPartnerColumn={dataSource.showPartnerColumn}
              emptyMessage={query !== "" ? "No deals match your search." : "No deals in this view."}
            />
          )}
        </div>

        {/* CHP-808: read-only master record — the PO drilling into the org's
            deal feed is not the deal owner (the BDM is), so owner-only write
            actions are suppressed (the backend 404s a non-owner on them). */}
        <DealDetailDrawer dealId={detailDealId} onClose={() => setDetailDealId(null)} readOnly />
      </SheetContent>
    </Sheet>
  );
}
